import { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLoading } from "../../../../../../hooks/useLoading";
import {
  loadPaymentSetupData,
  loadStripeConfig,
  determineStartingStep,
  StripeConfig,
} from "../../../../../../utils/firebase/paymentSetup";

interface PaymentSetupData {
  paymentMethod?: "customUrl" | "giveSpark";
  customDonationUrl?: string;
  stripeConnected?: boolean;
  progressTracker?: string;
}

interface UsePaymentSetupDataProps {
  charityId?: string;
  methods: UseFormReturn<any>;
}

interface UsePaymentSetupDataResult {
  loading: boolean;
  startingStep: number;
  error: string | null;
  paymentSetupData: PaymentSetupData | null;
  stripeConfig: StripeConfig | null;
}

const MINIMUM_LOADING_DURATION_MS = 800;

export const usePaymentSetupData = ({
  charityId,
  methods,
}: UsePaymentSetupDataProps): UsePaymentSetupDataResult => {
  const { loading, runWithLoading, setLoading } = useLoading(true);
  const [startingStep, setStartingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [paymentSetupData, setPaymentSetupData] = useState<PaymentSetupData | null>(null);
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null);

  useEffect(() => {
    const initializePaymentSetup = async () => {
      if (!charityId) {
        setLoading(false);
        return;
      }

      await runWithLoading(async () => {
        try {
          setError(null);

          const data = await loadPaymentSetupData(charityId);
          setPaymentSetupData(data);

          const config = await loadStripeConfig(charityId);
          setStripeConfig(config);

          if (data) {
            if (data.paymentMethod) {
              methods.setValue("paymentMethod", data.paymentMethod);
            }
            if (data.customDonationUrl) {
              methods.setValue("customDonationUrl", data.customDonationUrl);
            }

            const step = determineStartingStep(data);
            setStartingStep(step);
          } else {
            setStartingStep(0);
          }
        } catch (err) {
          console.error("Failed to load payment setup data:", err);
          setError("Failed to load payment setup data. Please refresh and try again.");
        }
      }, MINIMUM_LOADING_DURATION_MS);
    };

    initializePaymentSetup();
  }, [charityId, methods, runWithLoading, setLoading]);

  return {
    loading,
    startingStep,
    error,
    paymentSetupData,
    stripeConfig,
  };
};
